import { setAnalyticsCollectionEnabled } from 'firebase/analytics'
import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react'
import { analytics } from '@/services/firebase/config'
import { getStoredAnalyticsOptOut, setStoredAnalyticsOptOut } from '@/services/usage/consent'
import { PreferenceDoc } from '@/store/userData/preferences/types'
import { useGetPreferences } from '@/store/userData/preferences/useGetPreferences'

interface AnalyticsConsentContextValue {
  /** True when the user has opted out of analytics collection. */
  optedOut: boolean
  /** Persist the choice, apply it to the live Analytics instance, and mirror it to Firestore. */
  setOptedOut: (optedOut: boolean) => void
}

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null)

// Apply the choice to this device: persist it to localStorage (the boot-time source of
// truth) and toggle the live Analytics instance so it takes effect without a reload. The
// Firebase call no-ops when analytics isn't initialized (dev, unsupported, or not yet
// loaded — config.ts re-applies the stored value once it finishes initializing).
function applyConsent(optedOut: boolean): void {
  setStoredAnalyticsOptOut(optedOut)
  if (analytics) setAnalyticsCollectionEnabled(analytics, !optedOut)
}

/**
 * Owns the analytics opt-out BEHAVIOR: localStorage is the source of truth at boot (analytics inits
 * before auth). The cross-device mirror is PreferencesContext's, so this must nest inside that provider.
 */
export function AnalyticsConsentProvider({ children }: { children: ReactNode }) {
  const { state, updatePreferences } = useGetPreferences()
  const [optedOut, setOptedOutState] = useState(getStoredAnalyticsOptOut)

  const setOptedOut = useCallback(
    (next: boolean) => {
      setOptedOutState(next)
      applyConsent(next)
      // The write no-ops when signed out (updatePreferences guards on uid) — same as before.
      void updatePreferences(PreferenceDoc.Privacy, { analyticsOptOut: next })
    },
    [updatePreferences]
  )

  // Adopt the cross-device choice from the Firestore mirror once it's known. An explicit remote
  // value wins over the local default so an opt-out made on another device takes effect here. A
  // signed-out user keeps their device-local choice — PreferencesContext clears `privacy` to
  // null on sign-out, so this no-ops (a privacy preference shouldn't revert).
  const remoteOptOut = state.privacy?.analyticsOptOut
  useEffect(() => {
    if (typeof remoteOptOut === 'boolean') {
      setOptedOutState(remoteOptOut)
      applyConsent(remoteOptOut)
    }
  }, [remoteOptOut])

  return <AnalyticsConsentContext.Provider value={{ optedOut, setOptedOut }}>{children}</AnalyticsConsentContext.Provider>
}

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const ctx = useContext(AnalyticsConsentContext)
  if (!ctx) throw new Error('useAnalyticsConsent must be used within AnalyticsConsentProvider')
  return ctx
}
